// src/pages/Home.jsx
import React, { useState, useEffect } from 'react';
import PageTitle from '../components/SEO/PageTitle';
import FAQSection from '../components/SEO/FAQSection';
import { Link } from 'react-router-dom';
import { getAllJobs } from '../services/jobs';
import { getDocs, collection } from 'firebase/firestore';
import { db } from '../services/firebase';

const Home = () => {
    const [jobCount, setJobCount] = useState(0);
    const [userCount, setUserCount] = useState(0);
    const [recentJobs, setRecentJobs] = useState([]);
    const [loading, setLoading] = useState(true);

    const homeFAQ = [
        {
            question: "What is GradPath?",
            answer: "GradPath is South Africa's leading career platform that connects graduates and students with entry-level jobs, internships, and work-integrated learning opportunities. We help bridge the gap between education and employment by providing a free job board, CV builder, and career resources."
        },
        {
            question: "Is GradPath free for job seekers?",
            answer: "Yes! GradPath is completely free for job seekers. You can create a profile, upload your CV, apply for jobs, and access career resources at no cost. We believe in making career opportunities accessible to all graduates."
        },
        {
            question: "What types of jobs are available on GradPath?",
            answer: "We list entry-level positions, graduate programmes, internships, learnerships, and work-integrated learning opportunities across various industries including technology, finance, engineering, healthcare, retail, and more. All opportunities are tailored for recent graduates and students."
        },
        {
            question: "How do I apply for a job on GradPath?",
            answer: "Simply create a free account, complete your profile, and browse our job listings. When you find a role you're interested in, click 'Apply' and follow the application instructions. For internal jobs, you can apply directly through GradPath with one click."
        },
        {
            question: "Can employers post jobs on GradPath?",
            answer: "Yes! Employers and recruiters can create a recruiter account, get verified, and post job opportunities. We also offer premium features like sponsored job postings and candidate search to help companies find the right talent. Contact us for more information."
        },
        {
            question: "What cities does GradPath cover?",
            answer: "We cover all major cities across South Africa including Johannesburg, Cape Town, Durban, Pretoria, Port Elizabeth (Gqeberha), Bloemfontein, and many more. Our platform lists jobs from over 265+ cities nationwide, ensuring opportunities are accessible across the country."
        }
    ];

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const jobs = await getAllJobs();
            setJobCount(jobs.length);
            setRecentJobs(jobs.slice(0, 3));
            
            const usersSnapshot = await getDocs(collection(db, "users"));
            setUserCount(usersSnapshot.size);
        } catch (error) {
            console.error("Error loading data:", error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <i className="fas fa-spinner fa-spin text-4xl text-gradpath-green"></i>
                <span className="ml-3 text-gray-600">Loading...</span>
            </div>
        );
    }

    return (
        <div>
            <PageTitle 
                title="GradPath - Graduate Jobs & Internships in South Africa" 
                description="Find your dream job in South Africa - GradPath connects graduates with entry-level opportunities"
            />
            {/* Hero Section */}
            <section className="bg-hero-pattern bg-cover bg-center bg-fixed text-white py-20 px-4 text-center">
                <div className="container mx-auto max-w-4xl">
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                        Your Career Journey <span className="text-green-300">Starts Here</span>
                    </h1>
                    <p className="text-base md:text-lg lg:text-xl mb-8 text-gray-100 max-w-2xl mx-auto">
                        Find entry-level jobs, internships, and work-integrated learning opportunities tailored for recent
                        graduates and students in South Africa.
                    </p>

                    <div className="flex gap-4 justify-center flex-wrap">
                        <Link 
                            to="/jobs" 
                            className="bg-white text-gradpath-green hover:bg-gray-100 font-semibold px-6 py-3 rounded-lg transition-all inline-flex items-center gap-2 shadow-lg"
                        >
                            <i className="fas fa-search"></i> Browse All Jobs
                        </Link>
                        <Link 
                            to="/cv-creator" 
                            className="bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 font-semibold px-6 py-3 rounded-lg transition-all inline-flex items-center gap-2"
                        >
                            <i className="fas fa-file-alt"></i> Create CV
                        </Link>
                    </div>
                    
                    {/* Stats Section */}
                    <div className="flex justify-center gap-6 md:gap-12 mt-12 flex-wrap">
                        <div className="text-center bg-white/10 backdrop-blur-sm px-6 py-4 rounded-xl min-w-[120px]">
                            <i className="fas fa-building text-3xl text-green-300 mb-2"></i>
                            <p className="text-2xl md:text-3xl font-bold">265+</p>
                            <p className="text-xs md:text-sm">Cities Available</p>
                        </div>
                        <div className="text-center bg-white/10 backdrop-blur-sm px-6 py-4 rounded-xl min-w-[120px]">
                            <i className="fas fa-users text-3xl text-green-300 mb-2"></i>
                            <p className="text-2xl md:text-3xl font-bold">{userCount}+</p>
                            <p className="text-xs md:text-sm">Active Users</p>
                        </div>
                        <div className="text-center bg-white/10 backdrop-blur-sm px-6 py-4 rounded-xl min-w-[120px]">
                            <i className="fas fa-briefcase text-3xl text-green-300 mb-2"></i>
                            <p className="text-2xl md:text-3xl font-bold">800+</p>
                            <p className="text-xs md:text-sm">Hiring Companies</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-16 px-4 bg-white">
                <div className="container mx-auto max-w-6xl">
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-gradpath-dark mb-12">Why Choose GradPath?</h2>
                    <div className="grid md:grid-cols-3 gap-6 md:gap-8">
                        {/* Feature 1 */}
                        <div className="text-center p-6 bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1">
                            <i className="fas fa-filter text-4xl text-gradpath-green mb-4"></i>
                            <h3 className="text-lg md:text-xl font-bold mb-2">Smart Filters</h3>
                            <p className="text-sm md:text-base text-gray-600">Filter jobs by province, city, work type, and closing date</p>
                        </div>
                        
                        {/* Feature 2 */}
                        <div className="text-center p-6 bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1">
                            <i className="fas fa-file-pdf text-4xl text-gradpath-green mb-4"></i>
                            <h3 className="text-lg md:text-xl font-bold mb-2">CV & Cover Letter Builder</h3>
                            <p className="text-sm md:text-base text-gray-600">Create professional documents and download as PDF</p>
                        </div>
                        
                        {/* Feature 3 */}
                        <div className="text-center p-6 bg-gray-50 rounded-xl shadow-md hover:shadow-lg transition-all hover:-translate-y-1">
                            <i className="fas fa-chart-line text-4xl text-gradpath-green mb-4"></i>
                            <h3 className="text-lg md:text-xl font-bold mb-2">Smart Job Search</h3>
                            <p className="text-sm md:text-base text-gray-600">Find jobs with fuzzy search, autocomplete, and similar job recommendations</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Recent Jobs Section */}
            <section className="py-16 px-4 bg-gray-100">
                <div className="container mx-auto max-w-6xl">
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center text-gradpath-dark mb-12">Recent Opportunities</h2>
                    
                    {recentJobs.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-xl">
                            <i className="fas fa-briefcase text-5xl text-gray-300 mb-4"></i>
                            <p className="text-gray-500">No jobs available at the moment.</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {recentJobs.map(job => (
                                <div key={job.id} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-1 cursor-pointer group">
                                    <h3 className="text-lg md:text-xl font-bold text-gradpath-green mb-2 line-clamp-2">{job.title}</h3>
                                    <div className="flex flex-wrap gap-3 text-sm text-gray-600 mb-3">
                                        <span className="inline-flex items-center gap-1">
                                            <i className="fas fa-map-marker-alt text-gradpath-green"></i> 
                                            {job.city ? `${job.city}, ${job.province}` : job.province}
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <i className="fas fa-briefcase text-gradpath-green"></i> {job.workType}
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <i className="fas fa-calendar-alt text-gradpath-green"></i> {new Date(job.closingDate).toLocaleDateString('en-ZA')}
                                        </span>
                                    </div>
                                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                                        {job.description?.substring(0, 100)}...
                                    </p>
                                    {job.salary && (
                                        <p className="text-sm text-gray-500 mb-3">
                                            <i className="fas fa-money-bill-wave text-gradpath-green mr-1"></i> {job.salary}
                                        </p>
                                    )}
                                    <Link 
                                        to="/jobs"
                                        className="inline-flex items-center justify-center w-full bg-gradpath-green text-white font-semibold py-2 px-4 rounded-lg hover:bg-gradpath-dark transition-all group-hover:shadow-md"
                                    >
                                        View Details <i className="fas fa-arrow-right ml-2"></i>
                                    </Link>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <div className="text-center mt-10">
                        <Link 
                            to="/jobs" 
                            className="inline-flex items-center gap-2 border-2 border-gradpath-green text-gradpath-green font-semibold px-6 py-3 rounded-lg hover:bg-gradpath-green hover:text-white transition-all"
                        >
                            View All Jobs <i className="fas fa-arrow-right"></i>
                        </Link>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 px-4 bg-gradient-to-r from-gradpath-green to-gradpath-dark text-white">
                <div className="container mx-auto max-w-4xl text-center">
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">Ready to Launch Your Career?</h2>
                    <p className="text-lg mb-8 text-white/90">Join thousands of graduates who found their dream job through GradPath</p>
                    <div className="flex gap-4 justify-center flex-wrap">
                        <Link 
                            to="/jobs" 
                            className="bg-white text-gradpath-green hover:bg-gray-100 font-semibold px-8 py-3 rounded-lg transition-all inline-flex items-center gap-2 shadow-lg"
                        >
                            <i className="fas fa-search"></i> Find Jobs
                        </Link>
                        <Link 
                            to="/cv-creator" 
                            className="bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 font-semibold px-8 py-3 rounded-lg transition-all inline-flex items-center gap-2"
                        >
                            <i className="fas fa-file-alt"></i> Create CV
                        </Link>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <FAQSection 
                title="Frequently Asked Questions"
                questions={homeFAQ}
                className="bg-white"
            />
        </div>
    );
};

export default Home;